// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from '@reduxjs/toolkit';

import { addAsyncCases, updateStateStatus } from '../utils/utils';

import {
  fetchCompanyShows,
  createShow,
  duplicateShow,
  updateShow,
  deleteShow,
  fetchShowById,
  fetchShowBlocks,
  fetchBlockById,
  fetchBlockScripts,
  fetchShowScenes,
  createBlock,
  duplicateBlock,
  deleteBlock,
  updateShowImage,
  addSceneToShow,
  deleteSceneFromShow,
  updateBlock,
  fetchBlockEquipments,
  updateBlockImage,
  addBlockEquipment,
  addBlockEquipmentSettingValue,
  updateBlockEquipmentSettingValue,
  deleteBlockEquipmentSettingValue,
  deleteBlockEquipment,
  fetchShowDeployUuid,
  createDeploymentMapping,
  deployShow,
  addShowMember,
  removeShowMember,
  allShowMembers,
  deleteBlockWithNavigation,
  persistBlockReorder,
  duplicateBlockAndNavigate,
} from './showsActions';

import {
  setShowDeployState,
  reorderShowBlocksState,
  createBlockScriptLineState,
  deleteBlockScriptLineState,
  updateBlockScriptLineState,
  updateBlockScriptLineSubjectState,
  updateBlockScriptLineLocationState,
  createShowState,
  updateShowState,
  deleteShowState,
  addShowSceneState,
  deleteShowSceneState,
  createShowBlockState,
  updateShowBlockState,
  deleteShowBlockState,
  createBlockEquipmentState,
  deleteBlockEquipmentState,
  updateBlockEquipmentSettingsState,
  createDeployState,
  createShowMemberState,
  deleteShowMemberState,
} from './showsReducers';

const initialState = {
  shows: [],
  searchFilter: null, // e.g., { searchBy: 'query', query: 'test' } or { searchBy: 'author', userId: 123 }
  show: {
    show_uuid: null,
    show_blocks: [],
    show_equipment: [],
  },
  showScenes: [],
  showMembers: [],
  block: {
    lines: [],
    block_equipment: [],
  },
  clipboardBlock: null,
  deploy: null,
  navigationContext: null,
  status: {
    shows: 'idle',
    show: 'idle',
    showScenes: 'idle',
    block: 'idle',
    deploy: 'idle',
  }, // possible status state : 'idle' | 'loading' | 'succeeded' | 'failed'
  error: {
    shows: null,
    show: null,
    showScenes: null,
    block: null,
    deploy: null,
  },
};

const showsSlice = createSlice({
  name: 'shows',
  initialState,
  reducers: {
    clearError: state => {
      state.error = initialState.error;
    },
    clearShow: state => {
      state.showMembers = [];
      state.status.show = 'idle';
      state.error.show = null;
      state.error.shows = null;
      state.clipboardBlock = null;
    },
    clearBlock: state => {
      state.block = {
        block_equipment: [],
      };
      state.status.block = 'idle';
      state.error.block = null;
      state.error.showScenes = null;
    },
    clearDeployment: state => {
      state.deploy = null;
      state.status.deploy = 'idle';
      state.error.deploy = null;
    },
    blockError: (state, action) => {
      state.error.block = action.payload;
      state.status.block = 'failed';
    },
    copyBlockToClipboard: (state, action) => {
      state.clipboardBlock = action.payload;
    },
    setSearchFilter(state, action) {
      state.searchFilter = action.payload; // e.g. { searchBy: "query", query: "abc" }
    },
    clearSearchFilter(state) {
      state.searchFilter = null;
    },
    reorderShowBlocks: (state, action) => {
      const { movedBlock, newIndex } = action.payload;
      reorderShowBlocksState(state, { movedBlock, newIndex });
    },
    createBlockScriptLine: (state, action) => {
      const { newLine, subject, itemBefore } = action.payload;
      createBlockScriptLineState(state, newLine, subject, itemBefore);
    },
    updateBlockScriptLine: (state, action) => {
      updateBlockScriptLineState(state, action.payload);
    },
    deleteBlockScriptLine: (state, action) => {
      const { lineId, showBlockUuid } = action.payload;
      deleteBlockScriptLineState(state, lineId, showBlockUuid);
    },
    updateBlockScriptLineSubject: (state, action) => {
      updateBlockScriptLineSubjectState(state, action.payload);
    },
    updateBlockScriptLineLocation: (state, action) => {
      updateBlockScriptLineLocationState(state, action.payload);
    },
    setNavigationContext: (state, action) => {
      state.navigationContext = action.payload;
    },
    clearNavigationContext: state => {
      state.navigationContext = null;
    },
    // websocket handlers
    showRundownLaunchChanged: (state, action) => {
      setShowDeployState(state, action.payload);
    },
  },
  extraReducers: builder => {
    addAsyncCases(builder, [
      { action: fetchCompanyShows, key: 'shows' },
      { action: fetchBlockById, key: 'block' },
      { action: fetchShowScenes, key: 'showScenes' },
      { action: fetchShowDeployUuid, key: 'deploy' },
      { action: allShowMembers, key: 'showMembers' },
    ]);
    builder
      // get show by Id
      .addCase(fetchShowById.fulfilled, (state, action) => {
        state.deploy = null;
        state.error.deploy = null;
        state.show = action.payload;
        updateStateStatus(state, 'show', 'succeeded');
      })
      .addCase(fetchShowById.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      })
      // create show
      .addCase(createShow.pending, state => {
        updateStateStatus(state, 'shows', 'loading');
      })
      .addCase(createShow.fulfilled, (state, action) => {
        createShowState(state, action.payload);
        updateStateStatus(state, 'shows', 'succeeded');
      })
      .addCase(createShow.rejected, (state, action) => {
        updateStateStatus(state, 'shows', 'failed', action);
      })
      // duplicate show
      .addCase(duplicateShow.pending, state => {
        updateStateStatus(state, 'shows', 'loading');
      })
      .addCase(duplicateShow.fulfilled, (state, action) => {
        createShowState(state, action.payload);
        updateStateStatus(state, 'shows', 'succeeded');
      })
      .addCase(duplicateShow.rejected, (state, action) => {
        updateStateStatus(state, 'shows', 'failed', action);
      })
      // update show
      .addCase(updateShow.fulfilled, (state, action) => {
        updateShowState(state, action.payload);
        updateStateStatus(state, 'show', 'succeeded');
        updateStateStatus(state, 'shows', 'succeeded');
      })
      .addCase(updateShow.rejected, (state, action) => {
        updateStateStatus(state, 'shows', 'failed', action);
      })
      // delete show
      .addCase(deleteShow.fulfilled, (state, action) => {
        deleteShowState(state, action.payload);
        updateStateStatus(state, 'shows', 'succeeded');
      })
      .addCase(deleteShow.rejected, (state, action) => {
        updateStateStatus(state, 'shows', 'failed', action);
      })
      // fetch show blocks
      .addCase(fetchShowBlocks.fulfilled, (state, action) => {
        const existingBlocks = state.show.show_blocks || [];
        const incomingBlocks = action.payload || [];
        state.show.show_blocks = incomingBlocks
          .map(block => {
            const existingBlock = existingBlocks.find(b => b.block_uuid === block.block_uuid);

            return {
              ...block,
              order:
                existingBlock && existingBlock.order !== block.order ? block.order : block.order,
            };
          })
          .sort((a, b) => a.order - b.order);
        updateStateStatus(state, 'show', 'succeeded');
      })
      // update show image
      .addCase(updateShowImage.pending, state => {
        updateStateStatus(state, 'shows', 'loading');
      })
      .addCase(updateShowImage.fulfilled, (state, action) => {
        updateShowState(state, action.payload);
        updateStateStatus(state, 'shows', 'succeeded');
      })
      .addCase(updateShowImage.rejected, (state, action) => {
        updateStateStatus(state, 'shows', 'failed', action);
      })
      // add scene to show
      .addCase(addSceneToShow.pending, state => {
        updateStateStatus(state, 'showScenes', 'loading');
      })
      .addCase(addSceneToShow.fulfilled, (state, action) => {
        addShowSceneState(state, action.payload);
        updateStateStatus(state, 'showScenes', 'succeeded');
      })
      .addCase(addSceneToShow.rejected, (state, action) => {
        updateStateStatus(state, 'showScenes', 'failed', action);
      })
      .addCase(deleteSceneFromShow.fulfilled, (state, action) => {
        deleteShowSceneState(state, action.payload);
        updateStateStatus(state, 'showScenes', 'succeeded');
      })
      .addCase(deleteSceneFromShow.rejected, (state, action) => {
        updateStateStatus(state, 'showScenes', 'failed', action);
      })
      // get block scripts
      .addCase(fetchBlockScripts.fulfilled, (state, action) => {
        const { lines, subjects, locations } = action.payload;
        // Map each line and attach subject + location objects
        const mappedLines = lines.map(line => {
          return {
            ...line,
            subject: line.subject_uuid ? subjects[line.subject_uuid] : null,
            location: line.location_uuid ? locations[line.location_uuid] : null,
          };
        });
        state.block.lines = mappedLines;
        updateStateStatus(state, 'block', 'succeeded');
      })
      // create block
      .addCase(createBlock.pending, state => {
        updateStateStatus(state, 'show', 'loading');
      })
      .addCase(createBlock.fulfilled, (state, action) => {
        createShowBlockState(state, action.payload);
        updateStateStatus(state, 'show', 'succeeded');
      })
      .addCase(createBlock.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      })
      // duplicate block
      .addCase(duplicateBlock.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      })
      // duplicate block and navigate
      .addCase(duplicateBlockAndNavigate.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      })
      // update block
      .addCase(updateBlock.fulfilled, (state, action) => {
        updateShowBlockState(state, action.payload);
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(updateBlock.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // persist block reorder
      .addCase(persistBlockReorder.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // Upload and fetch image
      .addCase(updateBlockImage.fulfilled, (state, action) => {
        updateShowBlockState(state, action.payload);
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(updateBlockImage.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // delete block
      .addCase(deleteBlock.pending, state => {
        updateStateStatus(state, 'show', 'succeeded');
      })
      .addCase(deleteBlock.fulfilled, (state, action) => {
        deleteShowBlockState(state, action.payload);
        updateStateStatus(state, 'show', 'succeeded');
      })
      .addCase(deleteBlock.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      })
      // delete block and navigate
      .addCase(deleteBlockWithNavigation.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      })
      // get block equipment
      .addCase(fetchBlockEquipments.fulfilled, (state, action) => {
        state.block.block_equipment = action.payload;
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(fetchBlockEquipments.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // add block equipment
      .addCase(addBlockEquipment.pending, state => {
        updateStateStatus(state, 'block', 'loading');
      })
      .addCase(addBlockEquipment.fulfilled, (state, action) => {
        createBlockEquipmentState(state, action.payload);
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(addBlockEquipment.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // delete block equipment
      .addCase(deleteBlockEquipment.pending, state => {
        updateStateStatus(state, 'block', 'loading');
      })
      .addCase(deleteBlockEquipment.fulfilled, (state, action) => {
        deleteBlockEquipmentState(state, action.payload);
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(deleteBlockEquipment.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // add block equipment setting value
      .addCase(addBlockEquipmentSettingValue.pending, state => {
        updateStateStatus(state, 'block', 'loading');
      })
      .addCase(addBlockEquipmentSettingValue.fulfilled, (state, action) => {
        updateBlockEquipmentSettingsState(state, action.payload);
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(addBlockEquipmentSettingValue.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // update block equipment setting value
      .addCase(updateBlockEquipmentSettingValue.pending, state => {
        updateStateStatus(state, 'block', 'loading');
      })
      .addCase(updateBlockEquipmentSettingValue.fulfilled, (state, action) => {
        updateBlockEquipmentSettingsState(state, action.payload);
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(updateBlockEquipmentSettingValue.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // delete block equipment setting value
      .addCase(deleteBlockEquipmentSettingValue.pending, state => {
        updateStateStatus(state, 'block', 'loading');
      })
      .addCase(deleteBlockEquipmentSettingValue.fulfilled, (state, action) => {
        updateBlockEquipmentSettingsState(state, action.payload);
        updateStateStatus(state, 'block', 'succeeded');
      })
      .addCase(deleteBlockEquipmentSettingValue.rejected, (state, action) => {
        updateStateStatus(state, 'block', 'failed', action);
      })
      // deploy show
      .addCase(deployShow.pending, state => {
        updateStateStatus(state, 'deploy', 'loading');
      })
      .addCase(deployShow.fulfilled, (state, action) => {
        createDeployState(state, action.payload);
        updateStateStatus(state, 'deploy', 'succeeded');
      })
      .addCase(deployShow.rejected, (state, action) => {
        updateStateStatus(state, 'deploy', 'failed', action);
      })
      .addCase(createDeploymentMapping.pending, state => {
        updateStateStatus(state, 'deploy', 'loading');
      })
      .addCase(createDeploymentMapping.fulfilled, (state, action) => {
        createDeployState(state, action.payload);
        updateStateStatus(state, 'deploy', 'succeeded');
      })
      .addCase(createDeploymentMapping.rejected, (state, action) => {
        updateStateStatus(state, 'deploy', 'failed', action);
      })
      .addCase(addShowMember.fulfilled, (state, action) => {
        createShowMemberState(state, action.payload);
      })
      .addCase(addShowMember.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      })
      .addCase(removeShowMember.fulfilled, (state, action) => {
        deleteShowMemberState(state, action.payload);
      })
      .addCase(removeShowMember.rejected, (state, action) => {
        updateStateStatus(state, 'show', 'failed', action);
      });
  },
});

export const {
  clearError,
  clearShow,
  clearBlock,
  clearDeployment,
  blockError,
  setSearchFilter,
  clearSearchFilter,
  copyBlockToClipboard,
  reorderShowBlocks,
  createBlockScriptLine,
  deleteBlockScriptLine,
  updateBlockScriptLine,
  updateBlockScriptLineSubject,
  updateBlockScriptLineLocation,
  setNavigationContext,
  clearNavigationContext,
  showRundownLaunchChanged,
} = showsSlice.actions;

export const showsStatus = state => state.shows.status;
export const showsError = state => state.shows.error;

export default showsSlice.reducer;
